import { useForm, useStore } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Sparkles } from "lucide-react";
import { useMemo, useState } from "react";

import {
  AdminListPage,
  AdminListTable,
  contentColumns,
  DescriptionField,
  ROW_LINK_CLASS,
  TitleSlugFields,
  useAdminListCreate,
} from "~/components/cih/admin-list-page";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  createPlaybookFn,
  playbookQueryKeys,
  playbooksQueryOptions,
  type PlaybookListRow,
} from "~/lib/functions/cih/playbooks";
import { thoughtLeadersQueryOptions } from "~/lib/functions/cih/thought-leaders";
import {
  listSearchSchema,
  listSortDefaults,
  type SortColumn,
  sortListRows,
} from "~/lib/shared/admin-list-sort";
import { nextSortSearch } from "~/lib/shared/sort";

const PLAYBOOK_SORTS = ["title", "created"] as const;
type PlaybookSort = (typeof PLAYBOOK_SORTS)[number];

const PLAYBOOK_SORT_DEFAULTS = listSortDefaults<PlaybookSort>("title", [
  "created",
]);

const PLAYBOOK_SORT_COLUMNS: Record<
  PlaybookSort,
  SortColumn<PlaybookListRow>
> = {
  title: { kind: "text", value: (row) => row.title },
  created: { kind: "date", value: (row) => row.createdAt },
};

export const Route = createFileRoute("/admin/cih/playbooks/")({
  staticData: { title: "Playbooks" },
  validateSearch: listSearchSchema(PLAYBOOK_SORTS),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(playbooksQueryOptions()),
      context.queryClient.ensureQueryData(thoughtLeadersQueryOptions()),
    ]);
    return {};
  },
  component: PlaybooksPage,
});

function playbookColumns(
  thoughtLeaderNames: ReadonlyMap<string, string>,
): ColumnDef<PlaybookListRow>[] {
  return [
    {
      id: "title",
      header: "Title",
      cell: ({ row }) => (
        <Link
          to="/admin/cih/playbooks/$playbookId"
          params={{ playbookId: row.original.id }}
          className={ROW_LINK_CLASS}
        >
          {row.original.title}
        </Link>
      ),
    },
    {
      id: "thoughtLeader",
      header: "Thought leader",
      cell: ({ row }) => (
        <Link
          to="/admin/cih/thought-leaders/$thoughtLeaderId"
          params={{ thoughtLeaderId: row.original.thoughtLeaderId }}
          className="text-gray-700 hover:text-brand-600 hover:underline"
        >
          {thoughtLeaderNames.get(row.original.thoughtLeaderId) ?? "—"}
        </Link>
      ),
    },
    ...contentColumns<PlaybookListRow>({
      publicPath: (slug) => `/coaching-business/playbooks/${slug}`,
      pendingDraft: (row, className) =>
        row.pendingDraftId && (
          <Link
            to="/admin/cih/playbooks/$playbookId"
            params={{ playbookId: row.pendingDraftId }}
            className={className}
          >
            Draft pending
          </Link>
        ),
    }),
  ];
}

function PlaybooksPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const urlSearch = Route.useSearch();
  const sort = urlSearch.sort ?? PLAYBOOK_SORT_DEFAULTS.sort;
  const direction = urlSearch.dir ?? PLAYBOOK_SORT_DEFAULTS.direction(sort);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const create = useAdminListCreate(() => form.reset());

  const { data: playbooks, isLoading } = useQuery(
    playbooksQueryOptions(statusFilter || undefined),
  );
  const { data: thoughtLeaders } = useQuery(thoughtLeadersQueryOptions());

  const createMutation = useMutation({
    mutationFn: createPlaybookFn,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: playbookQueryKeys.all });
      create.finish("Playbook created.");
      navigate({
        to: "/admin/cih/playbooks/$playbookId",
        params: { playbookId: created.id },
      });
    },
  });

  const form = useForm({
    defaultValues: {
      title: "",
      slug: "",
      thoughtLeaderId: "",
      description: "",
    },
    onSubmit: ({ value }) => {
      createMutation.mutate({
        data: {
          title: value.title.trim(),
          slug: value.slug.trim() || undefined,
          thoughtLeaderId: value.thoughtLeaderId,
          description: value.description.trim(),
        },
      });
    },
  });

  const canCreate = useStore(form.store, ({ values }) =>
    Boolean(
      values.title.trim() &&
      values.description.trim() &&
      values.thoughtLeaderId,
    ),
  );

  const thoughtLeaderNames = useMemo(
    () => new Map((thoughtLeaders ?? []).map((tl) => [tl.id, tl.name])),
    [thoughtLeaders],
  );

  const columns = useMemo(
    () => playbookColumns(thoughtLeaderNames),
    [thoughtLeaderNames],
  );

  const shownPlaybooks = useMemo((): PlaybookListRow[] => {
    if (!playbooks) return [];
    const q = search.toLowerCase();
    const matches = q
      ? playbooks.filter(
          (p) =>
            p.title.toLowerCase().includes(q) ||
            (p.slug ?? "").toLowerCase().includes(q) ||
            (thoughtLeaderNames.get(p.thoughtLeaderId) ?? "")
              .toLowerCase()
              .includes(q),
        )
      : playbooks;
    return sortListRows(
      matches,
      sort,
      direction,
      PLAYBOOK_SORT_COLUMNS,
      (p) => p.title,
    );
  }, [playbooks, search, thoughtLeaderNames, sort, direction]);

  const changeSort = (column: PlaybookSort): void => {
    navigate({
      search: (previous) => ({
        ...previous,
        ...nextSortSearch({ sort, direction }, column, PLAYBOOK_SORT_DEFAULTS),
      }),
    });
  };

  const createFields = (
    <>
      <form.Field name="title">
        {(titleField) => (
          <form.Field name="slug">
            {(slugField) => (
              <TitleSlugFields
                create={create}
                idPrefix="playbook"
                titleLabel="Title"
                title={titleField.state.value}
                slug={slugField.state.value}
                titlePlaceholder="e.g., The $100M Offer playbook"
                slugPlaceholder="e.g., 100m-offer-playbook"
                onTitleChange={titleField.handleChange}
                onSlugChange={slugField.handleChange}
              />
            )}
          </form.Field>
        )}
      </form.Field>
      <form.Field name="thoughtLeaderId">
        {(field) => (
          <div>
            <Label htmlFor="playbook-thought-leader" className="mb-1">
              Thought leader
            </Label>
            <Select
              value={field.state.value || undefined}
              onValueChange={field.handleChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a thought leader" />
              </SelectTrigger>
              <SelectContent>
                {(thoughtLeaders ?? []).map((tl) => (
                  <SelectItem key={tl.id} value={tl.id}>
                    {tl.name}
                    {tl.organization ? ` — ${tl.organization}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </form.Field>
      <form.Field name="description">
        {(field) => (
          <DescriptionField
            id="playbook-description"
            value={field.state.value}
            placeholder="Brief description of this playbook..."
            onChange={field.handleChange}
          />
        )}
      </form.Field>
    </>
  );

  return (
    <AdminListPage
      title="Playbooks"
      description="Thought-leader-attributed business and marketing frameworks"
      itemName="Playbook"
      create={create}
      createFields={createFields}
      onCreateSubmit={() => form.handleSubmit()}
      canCreate={canCreate}
      isCreating={createMutation.isPending}
      createError={createMutation.error}
      statusFilter={{ value: statusFilter, onChange: setStatusFilter }}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: "Search playbooks or thought leaders...",
      }}
      isLoading={isLoading}
    >
      <AdminListTable
        rows={shownPlaybooks}
        columns={columns}
        sorts={PLAYBOOK_SORTS}
        sort={sort}
        direction={direction}
        onSortChange={changeSort}
        empty={{
          isFiltered: Boolean(search),
          icon: Sparkles,
          itemName: "Playbook",
          title: "No playbooks yet",
          description: "Create a thought-leader playbook to get started.",
          onCreate: create.open,
        }}
      />
    </AdminListPage>
  );
}
